if (!customElements.get('grouped-products')) {
  class GroupedProducts extends HTMLElement {
    constructor() {
      super();
      this.okendoReviewsSectionID = null;
      this.prefetchCache = new Map(); // Cache prefetched content
    }

    connectedCallback() {
      // Find okendo reviews section id
      this.okendoReviewsSectionID = document.querySelector('[id*="okendo_reviews_widget"]')?.closest('.shopify-section')?.getAttribute('id')?.replace('shopify-section-','')

      // Attach click listeners to grouped product options
      this.querySelectorAll('.grouped-product-option').forEach((swatch) => {
        swatch.addEventListener('click', this.handleSwatchChange.bind(this));
      });

      // Prefetch all options on page load (works for both mobile and desktop)
      // Use requestIdleCallback to not block page load
      if (window.requestIdleCallback) {
        requestIdleCallback(() => this.prefetchAllOptions());
      } else {
        setTimeout(() => this.prefetchAllOptions(), 1000);
      }
    }

    prefetchAllOptions() {
      const sectionId = this.getValidSectionId();

      this.querySelectorAll('.grouped-product-option').forEach((swatch) => {
        const url = swatch.getAttribute('href');

        if (!url || !sectionId || this.prefetchCache.has(url) || swatch.classList.contains('selected')) {
          return; // Skip if already cached or currently selected
        }

        const fetchUrl = this.getFetchUrl(url, sectionId);

        // Prefetch in background with low priority
        fetch(fetchUrl, { priority: 'low' })
          .then(response => response.text())
          .then(responseText => {
            this.prefetchCache.set(url, responseText);
          })
          .catch(err => {
            // Silent fail - will fetch normally on click if prefetch fails
            console.log('Prefetch failed:', err);
          });
      });
    }

    getValidSectionId() {
      const sectionId = this.dataset.section;
      if (!sectionId || sectionId === 'undefined' || sectionId === 'null') {
        return null;
      }

      return sectionId;
    }

    getFetchUrl(url, sectionId = this.getValidSectionId()) {
      if (!sectionId) {
        return url;
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}section_id=${sectionId}`;
    }

    resolveContext(triggerElement = this) {
      const minicartQuickbuyContainer = triggerElement.closest('.minicart-quickbuy-container');
      const quickbuyContainer = triggerElement.closest('.quickbuy-container');
      const isMinicartQuickbuy = !!minicartQuickbuyContainer;
      const isQuickbuy = !!quickbuyContainer;
      const isPageContext = !isQuickbuy && !isMinicartQuickbuy;
      const root = minicartQuickbuyContainer || quickbuyContainer || document;
      const sections = isPageContext ? ['.product-detail', '.breadcrumbs-list'] : ['.product-detail'];
      const productDetail = root.querySelector('.product-detail');
      const componentContainer = root.querySelector('[data-section-type="main-product"]');

      return {
        isPageContext,
        isQuickbuy,
        isMinicartQuickbuy,
        root,
        sections,
        productDetail,
        componentContainer,
        minicartContainer: minicartQuickbuyContainer
      };
    }

    prepareQuickbuyProductDetail(productDetail, productUrl, isMinicartQuickbuy = false) {
      if (!productDetail) {
        return;
      }

      const moreLink = productDetail.querySelector('.more');
      if (moreLink) {
        moreLink.setAttribute('href', productUrl);
        if (isMinicartQuickbuy) {
          moreLink.textContent = 'View Full Product Details';
        }
      }

      const titleElement = productDetail.querySelector('.detail .title');
      if (titleElement && !titleElement.querySelector('a')) {
        const titleLink = document.createElement('a');
        titleLink.setAttribute('href', productUrl);
        titleLink.innerHTML = titleElement.innerHTML;
        titleElement.innerHTML = '';
        titleElement.appendChild(titleLink);
      }

      productDetail.querySelectorAll('.show-gallery').forEach((galleryLink) => {
        galleryLink.classList.remove('show-gallery');
        galleryLink.setAttribute('href', productUrl);
      });

      productDetail.querySelectorAll('.not-in-quickbuy').forEach((element) => {
        element.remove();
      });

      productDetail.querySelectorAll('.only-in-quickbuy').forEach((element) => {
        element.classList.remove('only-in-quickbuy');
      });

      productDetail.querySelectorAll('.sticky-content-container').forEach((element) => {
        element.classList.remove('sticky-content-container');
      });

      productDetail.querySelectorAll('.store-availability-container').forEach((element) => {
        element.remove();
      });

      productDetail.querySelectorAll('[data-enable-history-state="true"]').forEach((element) => {
        element.setAttribute('data-enable-history-state', 'false');
      });

      productDetail.querySelectorAll('.gallery .thumbnails').forEach((thumbnails) => {
        thumbnails.classList.remove('mobile-only');
      });

      [
        'gallery--layout-carousel-beside',
        'gallery--layout-columns-1',
        'gallery--layout-columns-2',
        'gallery--layout-collage-1',
        'gallery--layout-collage-2'
      ].forEach((className) => {
        productDetail.querySelectorAll(`.${className}`).forEach((gallery) => {
          gallery.classList.remove(className);
          gallery.classList.add('gallery--layout-carousel-under');
        });
      });
    }

  async handleSwatchChange(e) {
    const context = this.resolveContext(e.currentTarget);

    // If we're in grid/collection Quick Buy, allow default navigation instead of AJAX
    if (context.isQuickbuy && !context.isMinicartQuickbuy) {
      return; // Allow the link to navigate normally
    }

    e.preventDefault();
    if (e.currentTarget.classList.contains('selected')) return;

    const url = e.currentTarget.getAttribute('href');
    const sectionId = this.getValidSectionId();
    if (!url) return

    // INSTANT VISUAL FEEDBACK - Update UI immediately before any work
    const clickedOption = e.currentTarget;

    // Store the previously selected option so we can revert on error
    const previouslySelected = this.querySelector('.grouped-product-option.selected');

    // 1. Update button states immediately
    this.querySelectorAll('.grouped-product-option').forEach(option => {
      option.classList.remove('selected');
    });
    clickedOption.classList.add('selected');

    // Force browser to repaint before continuing (critical for visual feedback)
    clickedOption.offsetHeight;

    // 2. Show loading overlay immediately
    const productDetail = context.productDetail;
    if (productDetail) {
      productDetail.classList.add('grouped-products-loading');
      // Force repaint
      productDetail.offsetHeight;
    }

    // Only update browser URL on the product page context.
    // Drawer/quickbuy contexts should keep users on their current page URL.
    if (context.isPageContext) {
      history.replaceState({}, '', url);
    }

    try {
      let responseText;

      // Check if we have prefetched content
      if (this.prefetchCache.has(url)) {
        // Even with prefetch, add tiny delay so user sees the visual feedback
        await new Promise(resolve => setTimeout(resolve, 100));
        responseText = this.prefetchCache.get(url);
        // Content is ready instantly - no network delay!
      } else {
        // Fallback to normal fetch if not prefetched
        const fetchUrl = this.getFetchUrl(url, sectionId);
        const response = await fetch(fetchUrl);
        responseText = await response.text();
      }

      // Remove animation attributes
      responseText = responseText.replaceAll('data-cc-animate', '')

      // Leave this commented-out, unless you want to animate the product form
      // theme.initAnimateOnScroll()

      const html = new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

      let newAccessoriesBlock = html.querySelector('.add-on-accessories-block');

      // elements to update
      const sections = context.sections;

      // List of elements to perserve
      // const perservedElements = '.app-block, .store-availability-container-outer'
      const perservedElements = '.upsell-theme-app-block-custom, .store-availability-container-outer'

      // Update page elements
      sections.forEach(section => {
        const responseSection = html.querySelector(section);
        const destination = context.root.querySelector(section);

        if (!responseSection || !destination) {
          return;
        }

        if (!context.isPageContext && section === '.product-detail') {
          this.prepareQuickbuyProductDetail(responseSection, url, context.isMinicartQuickbuy);
        }

        // Preserve some original elements within section
        const newElements = [...responseSection.querySelectorAll(perservedElements)]
        const originalElements = [...destination.querySelectorAll(perservedElements)]
        for (let i = 0; i < newElements.length; i++) {
          if (originalElements[i]) {
            newElements[i].replaceWith(originalElements[i])
          }
        }

        // Replace entire section html
        destination.replaceWith(responseSection)
      })

      // Remove loading overlay - content is ready
      const newProductDetail = context.root.querySelector('.product-detail');
      if (newProductDetail) {
        newProductDetail.classList.remove('grouped-products-loading');
      }

      // apply class to <main> element to override styles
      if (context.isPageContext) {
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.classList.add('grouped-products-view')
        }
      }

      // reinit add to cart form
      // Unload before reloading to prevent handler stacking
      const formContext = context.root;
      $('.product-form', formContext).trigger('unload-product-form');
      $('.product-form', formContext).trigger('load-product-form');

      // reinit gallery
      const componentContainer = context.root.querySelector('[data-section-type="main-product"]')
      if (componentContainer) {
        theme.initProductGallery(componentContainer);
      }

      // reinit other componenets
      $(componentContainer).closest('[data-components]').each(function () {
        const componentData = $(this).data('components');
        if (!componentData) {
          return;
        }

        componentData.split(',').forEach((component) => {
          $(document).trigger('cc:component:load', [component.trim(), componentContainer]);
        });
      });

      // update local pickup popup title
      const newProductTitle = context.root.querySelector('.product-form')?.querySelector('.title')?.textContent
      if(newProductTitle && document.querySelector('#store-availabilities-modal__product-title')) {
        document.querySelector('#store-availabilities-modal__product-title').textContent = newProductTitle
      }

      // Dispatch custom event to trigger slider reinitialization in minicart quickbuy
      const minicartQuickbuy = document.querySelector('.minicart-quickbuy-container.open');
      document.dispatchEvent(new CustomEvent('grouped-products:content-updated', {
        detail: {
          isMinicartQuickbuy: !!minicartQuickbuy,
          minicartContainer: minicartQuickbuy
        },
        bubbles: true
      }));

      // update accessories block
      let accessoriesBlock = document.querySelector('.add-on-accessories-block');
      if(context.isPageContext && accessoriesBlock) {

        if(newAccessoriesBlock) {
          // replace accessories block
          accessoriesBlock.replaceWith(newAccessoriesBlock);

          setTimeout(() => {
            // reload product-addon-js
            // reload product-upsell-slider-js
            reinitializeAfterFetch();

            // Trigger lazy loading for newly loaded images
            const newAccessoriesBlock = document.querySelector('.add-on-accessories-block');
            if (newAccessoriesBlock) {
              // Initialize product block manager for image handling
              if (typeof window.theme !== 'undefined' && window.theme.ProductBlockManager) {
                window.theme.ProductBlockManager.loadImages(newAccessoriesBlock);
              }
            }
          }, 1000);
        }
      }


      // reinit Klaviyo back in stock
      if (context.isPageContext) {
        this.loadAndInitKlaviyoBackInStock();
      }

      // update okendo reviews widget
      if (context.isPageContext && this.okendoReviewsSectionID) {
        const okendoResponse = await fetch(`${url}?section_id=${this.okendoReviewsSectionID}`);
        let okendoResponseText = await okendoResponse.text();
        const okendoHtml = new DOMParser().parseFromString(
          okendoResponseText,
          'text/html'
        );
        const newOkendoReviews = okendoHtml.querySelector('[id*="okendo_reviews_widget"]')
        const currentOkendoReviews = document.querySelector('[id*="okendo_reviews_widget"]')
        if (newOkendoReviews && currentOkendoReviews) {
          currentOkendoReviews.replaceWith(newOkendoReviews)
        }
      }

    } catch (error) {
      console.error('Error within grouped-products.js:', error);

      // Reset UI on error
      const errorProductDetail = context.root.querySelector('.product-detail');
      if (errorProductDetail) {
        errorProductDetail.classList.remove('grouped-products-loading');
      }

      // Revert button state to previously selected option
      this.querySelectorAll('.grouped-product-option').forEach(option => {
        option.classList.remove('selected');
      });
      if (previouslySelected) {
        previouslySelected.classList.add('selected');
      }

      // Optionally show an error message to the user
      console.warn('Failed to switch product variant');
    }
  }

  loadAndInitKlaviyoBackInStock() {
    // Initialize Klaviyo
    var klaviyo = window.klaviyo || [];
    klaviyo.init({
      account: "HKDrrQ",
      platform: "shopify"
    });
    klaviyo.enable("backinstock",{
      trigger: {
        product_page_text: "Notify Me When Available",
        product_page_class: "button",
        product_page_text_align: "center",
        product_page_margin: "0px",
        replace_anchor: false
      },
      modal: {
      headline: "{product_name}",
      body_content: "We'll notify you when this style is back in stock.",
      email_field_label: "Email",
      button_label: "Email Me When Available",
      subscription_success_label: "You're in! We'll let you know when it's back.",
      footer_content: '',
      additional_styles: "@import url('https://fonts.googleapis.com/css?family=Helvetica+Neue');",
      drop_background_color: "#000",
      background_color: "#fff",
      text_color: "#222",
      button_text_color: "#fff",
      button_background_color: "#ff9280",
      close_button_color: "#ccc",
      error_background_color: "#fcd6d7",
      error_text_color: "#C72E2F",
      success_background_color: "#d3efcd",
      success_text_color: "#1B9500"
      }
    });
  }
}

  customElements.define('grouped-products', GroupedProducts);
}